import * as THREE from 'three';
import { ENVIRONMENT, RENDERER } from '../constants/gameSettings.js';

export default class SceneManager {
    constructor(scene, physics) {
        this.scene = scene;
        this.physics = physics;
    }

    setupLights() {
        // Ambient light - softer ambient light for better contrast
        const ambientLight = new THREE.AmbientLight(0xE0E0FF, 0.5);
        this.scene.add(ambientLight);

        // Directional light (sun) - warmer sunset tone and lower position
        const directionalLight = new THREE.DirectionalLight(0xFFFFFF, 4.0);
        directionalLight.position.set(0, 100, 0); // Lower angle for sunset
        directionalLight.castShadow = RENDERER.SHADOWS_ENABLED;

        // Configure shadow properties for better quality
        directionalLight.shadow.mapSize.width = 4096;
        directionalLight.shadow.mapSize.height = 4096;
        directionalLight.shadow.camera.near = 0.5;
        directionalLight.shadow.camera.far = 150;
        directionalLight.shadow.camera.left = -70;
        directionalLight.shadow.camera.right = 70;
        directionalLight.shadow.camera.top = 70;
        directionalLight.shadow.camera.bottom = -70;
        directionalLight.shadow.bias = -0.0005;

        this.scene.add(directionalLight);
        this.sunLight = directionalLight;

        // Add an hemisphere light to simulate sunset sky
        const hemisphereLight = new THREE.HemisphereLight(
            0xFF7F50, // Sunset sky color
            0x786D5F, // Ground color - sandy tone
            0.5
        );
        this.scene.add(hemisphereLight);

        // Add a subtle back light to improve depth perception
        const backLight = new THREE.DirectionalLight(0xFFD700, 0.1);
        backLight.position.set(30, 10, 30);
        this.scene.add(backLight);

        return { ambientLight, sunLight: directionalLight };
    }

    createFloor(floorSize = ENVIRONMENT.TERRAIN_SIZE, terrainHeightMap = null) {
        // 200x200 segments = 40,401 vertices for smooth hills (1 draw call)
        const segments = terrainHeightMap ? 200 : 1;
        const groundGeometry = new THREE.PlaneGeometry(floorSize, floorSize, segments, segments);

        // Displace vertices using the height map BEFORE rotation
        // PlaneGeometry is in XY plane; after -PI/2 X rotation, Y -> Z and Z -> Y in world space
        let useVertexColors = false;
        if (terrainHeightMap) {
            const positions = groundGeometry.attributes.position;

            // Track height range for colour mapping
            let minH = Infinity, maxH = -Infinity;
            const heights = new Float32Array(positions.count);

            for (let i = 0; i < positions.count; i++) {
                const x = positions.getX(i);   // world X
                const y = positions.getY(i);   // becomes world -Z after -PI/2 X rotation
                const height = terrainHeightMap.getHeight(x, -y); // negate Y to match world Z
                positions.setZ(i, height);      // becomes world Y after rotation
                heights[i] = height;
                if (height < minH) minH = height;
                if (height > maxH) maxH = height;
            }
            positions.needsUpdate = true;
            groundGeometry.computeVertexNormals(); // correct lighting on slopes

            // --- Per-vertex altitude colouring ---
            // Valley (low)  → darker brown-ochre
            // Mid-slope     → warm golden sand
            // Hilltop (high) → bright pale sand
            const range = maxH - minH || 1; // avoid /0 on perfectly flat terrain
            const colors = new Float32Array(positions.count * 3);

            const valleyCol = new THREE.Color(0x8B6914);  // dark ochre
            const midCol    = new THREE.Color(0xCCA55D);  // base sand
            const peakCol   = new THREE.Color(0xF0DCA0);  // bright pale sand

            for (let i = 0; i < positions.count; i++) {
                // t ∈ [0,1] from valley to peak
                const t = (heights[i] - minH) / range;

                // Two-stop gradient: valley→mid (t 0-0.4), mid→peak (t 0.4-1.0)
                const col = new THREE.Color();
                if (t < 0.4) {
                    col.lerpColors(valleyCol, midCol, t / 0.4);
                } else {
                    col.lerpColors(midCol, peakCol, (t - 0.4) / 0.6);
                }

                colors[i * 3]     = col.r;
                colors[i * 3 + 1] = col.g;
                colors[i * 3 + 2] = col.b;
            }
            groundGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
            useVertexColors = true;
        }

        const groundMaterial = new THREE.MeshStandardMaterial({
            color: useVertexColors ? 0xffffff : 0xCCA55D, // white base when vertex colors tint it
            roughness: 0.8,
            metalness: 0.1,
            vertexColors: useVertexColors
        });

        const groundMesh = new THREE.Mesh(groundGeometry, groundMaterial);
        groundMesh.rotation.x = -Math.PI / 2; // Rotate to be flat
        groundMesh.position.y = 0;
        groundMesh.receiveShadow = RENDERER.SHADOWS_ENABLED;

        this.scene.add(groundMesh);
        this.groundMesh = groundMesh;

        // Set terrain height map on physics (replaces flat static plane)
        if (this.physics && this.physics.isInitialized) {
            if (terrainHeightMap) {
                this.physics.setTerrainHeightMap(terrainHeightMap);
            } else {
                const groundPosition = new THREE.Vector3(0, -0.1, 0);
                this.physics.createStaticPlane(groundPosition, new THREE.Vector3(0, 1, 0), 0.5);
            }
        }

        return groundMesh;
    }

    addGroundDecorations(floorSize = ENVIRONMENT.TERRAIN_SIZE, terrainHeightMap = null) {
        // Create several 'patches' of slightly different colored terrain
        // for visual interest
        const halfSize = floorSize / 2;

        for (let i = 0; i < 100; i++) {
            const patchSize = 5 + Math.random() * 50;
            const patchGeometry = new THREE.CircleGeometry(patchSize, 8);

            // Vary the color slightly for each patch
            const baseColor = new THREE.Color(0x999966);
            const colorVariation = 0.1;
            const r = baseColor.r + (Math.random() * colorVariation * 2 - colorVariation);
            const g = baseColor.g + (Math.random() * colorVariation * 2 - colorVariation);
            const b = baseColor.b + (Math.random() * colorVariation * 2 - colorVariation);

            const patchMaterial = new THREE.MeshStandardMaterial({
                color: new THREE.Color(r, g, b),
                roughness: 1.0,
                metalness: 0.0,
                flatShading: true
            });

            const patch = new THREE.Mesh(patchGeometry, patchMaterial);

            const px = Math.random() * floorSize - halfSize;
            const pz = Math.random() * floorSize - halfSize;
            const terrainY = terrainHeightMap
                ? terrainHeightMap.getHeight(px, pz) + 0.02
                : -0.09;

            patch.rotation.x = -Math.PI / 2;
            patch.position.set(px, terrainY, pz);
            this.scene.add(patch);
        }
    }
}
